import { AxiosResponse } from "axios";
import {
  AddRounded,
  CloseRounded,
  DeleteOutlineRounded,
  EditRounded,
  SearchRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import Loading from "../../../../components/loading/loading";
import RequiredMark from "../../../../components/required_mark/required_mark";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { queryClient } from "../../../../providers/query/query";
import useUser from "../../../../providers/user/user_provider";
import { black, greyEnabled, purple, purpleLight, red, redLight } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { HospitalMasterItem, HospitalMasterItemPayload } from "../../model/hospital";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";
import { DEFAULT_ORG_ID } from "../../../../constants";

const ROWS_PER_PAGE = 8;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export interface HospitalMasterListProps {
  /** Capitalized singular label, e.g. "Scheme" / "Facility" / "Speciality". */
  itemLabel: string;
  /** Capitalized plural label, e.g. "Schemes" / "Facilities" / "Specialities". */
  itemLabelPlural: string;
  /** Unique react-query cache key for this resource, e.g. "hospital-schemes". */
  queryKey: string;
  getItems: (filters: { page: number; size: number; orgId?: number | null }) => Promise<AxiosResponse>;
  createItem: (payload: HospitalMasterItemPayload) => Promise<AxiosResponse>;
  updateItem: (
    id: number | string,
    payload: HospitalMasterItemPayload
  ) => Promise<AxiosResponse>;
  deleteItem: (id: number | string) => Promise<AxiosResponse>;
}

export default function HospitalMasterList({
  itemLabel,
  itemLabelPlural,
  queryKey,
  getItems,
  createItem,
  updateItem,
  deleteItem,
}: HospitalMasterListProps) {
  const { hasPermission, userPermissions } = useUser();
  const { showSnackbar } = useFeedback();
  const { selectedOrgId } = useSelectedOrgStore();
  // A Platform Admin picking an org below creates the item there;
  // otherwise falls back to the caller's own org, matching create_user.tsx.
  const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
  const lowerLabel = itemLabel.toLowerCase();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<HospitalMasterItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HospitalMasterItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, isError } = useQuery([queryKey, "all", selectedOrgId], async () => {
    const response = await getItems({ page: 1, size: 1000, orgId: selectedOrgId });
    return response.data.data as { total: number; list: HospitalMasterItem[] };
  });

  const fullList = data?.list || [];

  useEffect(() => {
    setPage(1);
  }, [searchValue, selectedOrgId]);

  const filteredList = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return fullList;
    return fullList.filter(
      (item) =>
        item.name?.toLowerCase().includes(term) ||
        (item.code || "").toLowerCase().includes(term)
    );
  }, [fullList, searchValue]);

  const count = filteredList.length;
  const pageItems = filteredList.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  const startRange = (page - 1) * ROWS_PER_PAGE + 1;
  const endRange = Math.min(page * ROWS_PER_PAGE, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && pageItems.length > 0;
  const showEmpty = !isLoading && !isError && filteredList.length === 0;

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleAddClick = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleEditClick = (item: HospitalMasterItem) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleFormClose = () => setFormOpen(false);

  const handleFormSaved = (message: string) => {
    showSnackbar(message);
    queryClient.invalidateQueries({ queryKey: [queryKey] });
    setFormOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteItem(deleteTarget.id)
      .then(() => {
        showSnackbar(`${itemLabel} deleted successfully`);
        queryClient.invalidateQueries({ queryKey: [queryKey] });
        setDeleteTarget(null);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            `Something went wrong while deleting the ${lowerLabel}.`
        );
      })
      .finally(() => setIsDeleting(false));
  };

  return (
    <>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box mb={2}>
          <Typography variant="h6" fontWeight={700} color={black}>
            Hospital {itemLabelPlural}
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            Manage the list of {lowerLabel}s hospitals can be tagged with.
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              fullWidth
              placeholder={`Search by ${lowerLabel} name`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <OrgSelector lockToOwnOrg />
            {hasPermission("hospitals", "create") && (
              <Button
                onClick={handleAddClick}
                variant="contained"
                disableElevation
                startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  bgcolor: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#7A2074" },
                }}
              >
                Add {itemLabel}
              </Button>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the {lowerLabel}s list. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message={`No ${itemLabelPlural.toLowerCase()} found`}
            hint={`Try adjusting your search, or add a new ${lowerLabel}.`}
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[{ label: "Name" }, { label: "Code" }, { label: "Actions" }]}
              lastColumnWidth={110}
              align="left"
            />
            {pageItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell label="Name">
                  <Typography fontWeight={600} color={black} noWrap>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell label="Code">
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {item.code || "-"}
                  </Typography>
                </TableCell>
                <TableCell isLast lastColumnWidth={110}>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    {hasPermission("hospitals", "update") && (
                      <Tooltip title={`Edit ${lowerLabel}`}>
                        <IconButton
                          onClick={() => handleEditClick(item)}
                          sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                        >
                          <EditRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("hospitals", "delete") && (
                      <Tooltip title={`Delete ${lowerLabel}`}>
                        <IconButton
                          onClick={() => setDeleteTarget(item)}
                          sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                        >
                          <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        </CardActions>
      )}

      <HospitalMasterFormDialog
        open={formOpen}
        itemLabel={itemLabel}
        editingItem={editingItem}
        createItem={(payload) => createItem({ ...payload, orgId: targetOrgId })}
        updateItem={updateItem}
        onClose={handleFormClose}
        onSaved={handleFormSaved}
        onError={(message) => showSnackbar(message)}
      />

      <HospitalMasterDeleteDialog
        open={!!deleteTarget}
        itemLabel={itemLabel}
        itemName={deleteTarget?.name}
        isDeleting={isDeleting}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}

interface HospitalMasterFormDialogProps {
  open: boolean;
  itemLabel: string;
  editingItem: HospitalMasterItem | null;
  createItem: (payload: HospitalMasterItemPayload) => Promise<AxiosResponse>;
  updateItem: (
    id: number | string,
    payload: HospitalMasterItemPayload
  ) => Promise<AxiosResponse>;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

function HospitalMasterFormDialog({
  open,
  itemLabel,
  editingItem,
  createItem,
  updateItem,
  onClose,
  onSaved,
  onError,
}: HospitalMasterFormDialogProps) {
  const isEdit = !!editingItem;
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const lowerLabel = itemLabel.toLowerCase();

  useEffect(() => {
    if (open) {
      setName(editingItem?.name || "");
      setCode(editingItem?.code || "");
    }
  }, [open, editingItem]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setSubmitting(true);
    const payload: HospitalMasterItemPayload = {
      name: trimmedName,
      code: code.trim() || null,
    };
    const request = isEdit ? updateItem(editingItem!.id, payload) : createItem(payload);
    request
      .then(() => {
        onSaved(`${itemLabel} ${isEdit ? "updated" : "added"} successfully`);
      })
      .catch((e) => {
        onError(
          e?.response?.data?.error?.message ||
            `Something went wrong while ${isEdit ? "updating" : "adding"} the ${lowerLabel}.`
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
      >
        <Typography variant="h6" fontWeight={700} color={black}>
          {isEdit ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={submitting}>
          <CloseRounded sx={{ color: greyEnabled }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={2.5}>
          <TextField
            label={
              <>
                Name <RequiredMark />
              </>
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            disabled={submitting}
            autoFocus
          />
          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            size="small"
            disabled={submitting}
          />
        </Box>

        <Box display="flex" gap={1.5} width="100%" mt={4}>
          <Button
            fullWidth
            disabled={submitting}
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={submitting || !name.trim()}
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
            sx={{
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

interface HospitalMasterDeleteDialogProps {
  open: boolean;
  itemLabel: string;
  itemName?: string;
  isDeleting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function HospitalMasterDeleteDialog({
  open,
  itemLabel,
  itemName,
  isDeleting = false,
  onClose,
  onConfirm,
}: HospitalMasterDeleteDialogProps) {
  return (
    <Dialog open={open} onClose={() => !isDeleting && onClose()} PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent
        sx={{
          width: 400,
          maxWidth: "90vw",
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: redLight,
            mb: 2,
          }}
        >
          <WarningAmberRounded sx={{ color: red, fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color={black} textAlign="center">
          Delete this {itemLabel.toLowerCase()}?
        </Typography>
        <Typography variant="body2" color={greyEnabled} textAlign="center" mt={0.5}>
          {itemName ? (
            <>
              You're about to delete <strong>{itemName}</strong>. This action cannot be undone.
            </>
          ) : (
            "This action cannot be undone."
          )}
        </Typography>

        <Box display="flex" gap={1.5} width="100%" mt={4}>
          <Button
            fullWidth
            disabled={isDeleting}
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={isDeleting}
            onClick={onConfirm}
            variant="contained"
            disableElevation
            startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
            sx={{
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            Delete
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
